import React, { Fragment, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { useStores } from '../../hooks/use-stores';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import {
    Block,
    SpaceBetweenDiv,
    BigModal,
    ThemedButton,
    Heading2,
    TextDefault,
} from '@awarego-ui/components';
import AddManager from '../../components/super/add-manager';
import AddManagerDef from '../../forms/add-manager';
import { useLocalObservable } from 'mobx-react';
import TableClient from '../../components/table/TableClient';
import { Menu, MenuItem } from '@mui/material';
import { DarkArrowTooltipIcon } from '@awarego-ui/components';
import services from '../../services';

function PartnerAdmins() {
    const { partnerId } = useParams();
    const { commonStore, authStore } = useStores();
    const [showAddManagerModal, setShowAddManagerModal] = useState(false);
    const [managers, setManagers] = useState([]);
    const [loadingManagers, setLoadingManagers] = useState(true);
    const [addingManager, setAddingManager] = useState(false);
    const [addManagerError, setAddManagerError] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [targetAdmin, setTargetAdmin] = useState(null);

    const partnerName = React.useMemo(() => {
        const partner = authStore.currentUser?.superForAccounts?.find(
            (x) => x.id === Number(partnerId)
        );
        return partner?.name || 'Partner';
    }, [partnerId, authStore.currentUser]);

    const localStore = useLocalObservable(() => {
        let managerForm = new AddManagerDef(
            { values: { role: 1 } },
            {
                onSuccess() {
                    doAddManager(this);
                },
            }
        );
        return { managerForm };
    });

    useEffect(() => {
        loadManagers();
    }, [partnerId]);

    const loadManagers = async () => {
        setLoadingManagers(true);
        try {
            // TODO: Replace with actual API endpoint when available
            // For now, this will fail gracefully
            const result = await services.Partners.managersService(
                partnerId
            ).list();
            setManagers(result || []);
        } catch (error) {
            console.error('Failed to load managers:', error);
            setManagers([]);
        } finally {
            setLoadingManagers(false);
        }
    };

    const doAddManager = async (form) => {
        setAddingManager(true);
        setAddManagerError(null);
        try {
            await services.Partners.managersService(partnerId).create(
                form.values()
            );
            setShowAddManagerModal(false);
            commonStore.success('Admin added!');
            await loadManagers();
        } catch (error) {
            setAddManagerError(error.response?.data?.msg || 'Failed to add admin');
        } finally {
            setAddingManager(false);
        }
    };

    const doRemoveManager = (userId, name, email) => {
        commonStore.showConfirm(
            `Admin ${name || email} will be removed from ${partnerName}`,
            'Remove',
            `Remove ${name || email}?`,
            async () => {
                try {
                    await services.Partners.managersService(partnerId).delete(
                        userId
                    );
                    commonStore.success('Admin removed!');
                    await loadManagers();
                } catch (error) {
                    commonStore.error(
                        error.response?.data?.msg || 'Failed to remove admin'
                    );
                }
            },
            true
        );
    };

    const doSendLoginInfo = (managerEmail) => {
        commonStore.showConfirm(
            `Do you want to send an email to ${managerEmail} with a login link?`,
            'Send email',
            'Send login email',
            async () => {
                try {
                    await authStore.signin(
                        managerEmail,
                        null,
                        null,
                        null,
                        `/partner/${partnerId}/companies`,
                        'login-info'
                    );
                    commonStore.success('Login email sent!');
                } catch (error) {
                    commonStore.error('Failed to send login email');
                }
            },
            false
        );
    };

    const handleMenuOpen = (e, admin) => {
        setTargetAdmin(admin);
        setAnchorEl(e.currentTarget);
    };

    const handleMenuClose = () => {
        setTargetAdmin(null);
        setAnchorEl(null);
    };

    const columns = React.useMemo(
        () => [
            {
                Header: 'Email',
                accessor: 'email',
            },
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                Header: ' ',
                Cell: (x) => buildExtraActionsColumn(x.row.original),
            },
        ],
        [partnerName]
    );

    const buildExtraActionsColumn = (row) => {
        return (
            <>
                <DarkArrowTooltipIcon
                    customFunction={() => {
                        doRemoveManager(row.user_id || row.id, row.name, row.email);
                    }}
                    row={row}
                    title="Delete Admin"
                    icon={'delete'}
                />
                <DarkArrowTooltipIcon
                    customFunction={(e) => handleMenuOpen(e, row)}
                    row={row}
                    title="More"
                    icon={'more'}
                />
            </>
        );
    };

    if (loadingManagers) {
        return <ThemedSpinner />;
    }

    return (
        <Fragment>
            <Block>
                <SpaceBetweenDiv ml={16} pr={32}>
                    <div>
                        <Heading2 displayBlock>Admins</Heading2>
                        <TextDefault lighter displayBlock>
                            Manage {partnerName} administrators
                        </TextDefault>
                    </div>
                    <ThemedButton
                        mode="secondary"
                        text="Add admin"
                        onClick={() => {
                            localStore.managerForm.reset();
                            setShowAddManagerModal(true);
                            setAddManagerError(null);
                        }}
                    />
                </SpaceBetweenDiv>
                {managers.length > 0 ? (
                    <TableClient
                        columns={columns}
                        data={managers}
                        hidePagination={managers.length < 10}
                        initialPageSize={10}
                    />
                ) : (
                    <TextDefault lighter ml={16} mt={16}>
                        No admins found. Add your first admin to get started.
                    </TextDefault>
                )}
            </Block>
            <BigModal
                isOpen={showAddManagerModal}
                onRequestClose={() => {
                    setShowAddManagerModal(false);
                    setAddManagerError(null);
                }}
                heading="Add new admin"
                modalContent={
                    <AddManager
                        error={addManagerError}
                        managerForm={localStore.managerForm}
                        addManagerError={addManagerError}
                        hideRole
                    />
                }
                modalFooterContent={
                    addingManager ? (
                        <ThemedButton
                            mode="primary"
                            type="button"
                            disabled
                            $saving={true}
                            text="Saving"
                            $center={true}
                        />
                    ) : (
                        <Fragment>
                            <ThemedButton
                                mode="secondary"
                                text="Cancel"
                                onClick={() => {
                                    setShowAddManagerModal(false);
                                    setAddManagerError(null);
                                }}
                            />
                            <ThemedButton
                                mode="primary"
                                text="Add"
                                onClick={() => {
                                    localStore.managerForm.submit();
                                }}
                            />
                        </Fragment>
                    )
                }
            />
            {targetAdmin && (
                <Menu
                    id="simple-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={targetAdmin != null}
                    onClose={handleMenuClose}
                    elevation={1}
                >
                    <MenuItem
                        onClick={(e) => {
                            doSendLoginInfo(targetAdmin.email);
                            handleMenuClose(e);
                        }}
                    >
                        Send login link
                    </MenuItem>
                </Menu>
            )}
        </Fragment>
    );
}

export default observer(PartnerAdmins);
